/**
 * Angular-aware Practice-mode store.
 *
 * Thin signal wrapper around the pure {@link practice-repo} module. The
 * store is the single source of truth for the Practice setup screen
 * (last selections), the result modal (newly-recorded entry), and the
 * history view.
 *
 * Per the plan's Practice MVP: badges are NOT evaluated or persisted in
 * Practice mode — only history + last selections. Career mode (Etap 2)
 * will introduce a separate profile store.
 */

import { Injectable, computed, signal, type Signal } from "@angular/core";

import { CASTOFFPRO_GAMIFICATION_PRACTICE } from "../storage/storage-keys";
import type { StorageLike } from "../storage/migrate-vf-to-castoffpro";
import {
  EMPTY_PRACTICE_STATE,
  appendHistory,
  entryFromResult,
  loadPracticeState,
  patchLastSelections,
  savePracticeState,
  type PracticeLastSelections,
  type PracticeSessionEntry,
  type PracticeState,
} from "./practice-repo";
import type {
  ManeuverDeclaration,
  ManeuverResult,
} from "./maneuver-state-machine";

@Injectable({ providedIn: "root" })
export class PracticeStore {
  private readonly storage: StorageLike | null = safeLocalStorage();
  private readonly _state = signal<PracticeState>(
    loadPracticeState(this.storage, CASTOFFPRO_GAMIFICATION_PRACTICE),
  );

  readonly history: Signal<readonly PracticeSessionEntry[]> = computed(
    () => this._state().history,
  );
  readonly lastSelections: Signal<PracticeLastSelections> = computed(
    () => this._state().lastSelections,
  );
  /** Most recent entry, if any — useful for the result modal. */
  readonly latest: Signal<PracticeSessionEntry | null> = computed(() => {
    const h = this._state().history;
    return h.length > 0 ? h[h.length - 1] : null;
  });

  /**
   * Record a completed/failed/aborted maneuver. Appends to history (FIFO
   * cap 50) and persists. Returns the entry that was stored.
   */
  record(
    declaration: ManeuverDeclaration,
    result: ManeuverResult,
    timestampMs: number = Date.now(),
  ): PracticeSessionEntry {
    const entry = entryFromResult(declaration, result, timestampMs);
    const next = appendHistory(this._state(), entry);
    this.commit(next);
    return entry;
  }

  /** Update the cached setup choices used to prefill the Practice screen. */
  setLastSelections(patch: Partial<PracticeLastSelections>): void {
    this.commit(patchLastSelections(this._state(), patch));
  }

  /** Wipe the history (kept selections). Used by a "clear results" action. */
  clearHistory(): void {
    this.commit({ ...this._state(), history: [] });
  }

  /** Hard reset — clears everything including last selections. Used in tests. */
  reset(): void {
    this.commit(EMPTY_PRACTICE_STATE);
  }

  private commit(state: PracticeState): void {
    this._state.set(state);
    savePracticeState(this.storage, CASTOFFPRO_GAMIFICATION_PRACTICE, state);
  }
}

function safeLocalStorage(): StorageLike | null {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage;
  } catch {
    return null;
  }
}
